import React, { useState, useEffect, useReducer, useContext, useRef } from "react";
import openSocket from "../../services/socket-io";

import makeStyles from "@mui/styles/makeStyles";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { ListSubheader } from "@mui/material";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  ticketsListWrapperPending: {
    position: "relative",
    display: "flex",
    maxHeight: "290px",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  ticketsList: {
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderTop: `2px solid ${theme.palette.divider}`,
  },
  ticketsListHeader: {
    color: theme.palette.text.primary,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ticketsCount: {
    fontWeight: "normal",
    color: theme.palette.text.secondary,
    marginLeft: "8px",
    fontSize: "14px",
  },
  noTicketsText: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "14px",
    lineHeight: "1.4",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = Array.isArray(action.payload) ? action.payload : [];
    newTickets.forEach(ticket => {
      const ticketIndex = state.findIndex(t => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });
    return [...state];
  }

  if (action.type === "APPEND_TICKETS") {
    const newTickets = Array.isArray(action.payload) ? action.payload : [];
    // Filter out tickets that already exist in state (avoid duplicates)
    const filteredTickets = newTickets.filter(
      newTicket => !state.some(existingTicket => existingTicket.id === newTicket.id)
    );
    // Append new tickets to the end (back of the array)
    return [...state, ...filteredTickets];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex(t => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }
    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex(t => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }
    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex(t => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }
    return [...state];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex(t => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex(t => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsList = ({ status, searchParam, showAll, selectedQueueIds }) => {
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const [cinza, setCinza] = useState();

  // Ref to know if we are loading more pages (append mode)
  const isAppending = useRef(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    isAppending.current = false; // reset append flag when filters change
  }, [status, searchParam, showAll, selectedQueueIds]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    queueIds: JSON.stringify(selectedQueueIds),
  });

  // Handle new tickets from the hook
  useEffect(() => {
    if (!tickets || tickets.length === 0) return;

    if (isAppending.current) {
      // Append mode: add new tickets to the back of the array
      dispatch({ type: "APPEND_TICKETS", payload: tickets });
      isAppending.current = false;
    } else {
      // Fresh load (page 1 or after filter change)
      dispatch({ type: "LOAD_TICKETS", payload: tickets });
    }
  }, [tickets]);

  useEffect(() => {
    async function fetchCinza() {
      let filaCinza = await api
        .get(`/settings/check/msg-fila-cinza`)
        .then(v => v.data);
      filaCinza = filaCinza == 0 ? true : false;
      setCinza(!filaCinza);
    }
    fetchCinza();
  }, []);

  useEffect(() => {
    const socket = openSocket();
    const filaCinza = cinza;

    const shouldUpdateTicket = ticket =>
      (!ticket.userId || ticket.userId === user?.id || showAll) &&
      ((!ticket.queueId && filaCinza) ||
        ticket.userId === user?.id ||
        selectedQueueIds.indexOf(ticket.queueId) > -1);

    const notBelongsToUserQueues = ticket =>
      ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

    const notFilaCinza = ticket =>
      !showAll && ticket.status === "pending" && !filaCinza;

    /** Só mantém o ticket nesta lista se o status bater (open vs pending). */
    const belongsToThisList = ticket => {
      if (!ticket || !status) return true;
      return ticket.status === status;
    };

    const onConnect = () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
    };

    const onTicket = data => {
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
        return;
      }

      if (data.action === "delete" || data.action === "removeFromList") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
        return;
      }

      if (data.action === "update" && data.ticket) {
        if (!belongsToThisList(data.ticket)) {
          // Saiu desta coluna (ex.: open → pending) — remove da lista atual
          dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
          return;
        }

        if (shouldUpdateTicket(data.ticket)) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
        }

        if (notBelongsToUserQueues(data.ticket) && notFilaCinza(data.ticket)) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
        }
      }
    };

    const onAppMessage = data => {
      if (
        (data.action === "create" || data.action === "update") &&
        data.ticket
      ) {
        if (!belongsToThisList(data.ticket)) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
          return;
        }

        if (shouldUpdateTicket(data.ticket)) {
          dispatch({
            type: "UPDATE_TICKET_UNREAD_MESSAGES",
            payload: data.ticket,
          });
        }
      }
    };

    const onContact = data => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    };

    socket.on("connect", onConnect);
    socket.on("ticket", onTicket);
    socket.on("appMessage", onAppMessage);
    socket.on("contact", onContact);
    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("ticket", onTicket);
      socket.off("appMessage", onAppMessage);
      socket.off("contact", onContact);
    };
  }, [status, showAll, user, selectedQueueIds, cinza]);

  const loadMore = () => {
    if (!hasMore || loading) return;
    isAppending.current = true; // next fetch will be appended
    setPageNumber(prev => prev + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div
      className={[
        classes.ticketsListWrapper,
        status === "pending" && classes.ticketsListWrapperPending,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {status === "open" && (
            <ListSubheader className={classes.ticketsListHeader}>
              <div>
                {i18n.t("ticketsList.assignedHeader")}
                <span className={classes.ticketsCount}>
                  {ticketsList.length}
                </span>
              </div>
            </ListSubheader>
          )}
          {status === "pending" && (
            <ListSubheader className={classes.ticketsListHeader}>
              <div>
                {i18n.t("ticketsList.pendingHeader")}
                <span className={classes.ticketsCount}>
                  {ticketsList.length}
                </span>
              </div>
            </ListSubheader>
          )}
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map(ticket => (
                <TicketListItem ticket={ticket} key={ticket.id} />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </div>
  );
};

export default TicketsList;

